const logging = require('../../ot/logging.js');
const listenForTracksEnded = require('./listenForTracksEnded.js')();
const Watcher = require('../../helpers/Watcher');
const canBeOrientatedMixin = require('./can_be_oriented_mixin.js');
const eventing = require('../../helpers/eventing');

const TIMEOUT = 3000;

/**
 * PluginVideoElementWrapper
 *
 * @package
 * @class
 * @param {Object} options
 * @param {String} options.fitMode
 * @param {Function} errorHandler callback for when there are errors
 * @param {Function} orientationChangedHandler callback for when the orientation changes
 * @return {PluginVideoElementWrapper}
 */
function PluginVideoElementWrapper(
  { fitMode },
  errorHandler,
  orientationChangedHandler
) {
  let pluginVideoContainer;
  let parentDomElement;

  /**
   * Get the underlying DOM element
   * @return {Element}
   */
  this.domElement = () => (pluginVideoContainer ? pluginVideoContainer.domElement : undefined);
  this.videoWidth = () => (pluginVideoContainer ? pluginVideoContainer.videoWidth() : undefined);
  this.videoHeight = () => (pluginVideoContainer ? pluginVideoContainer.videoHeight() : undefined);
  this.imgData = () => (pluginVideoContainer ? pluginVideoContainer.getImgData() : null);
  this.isAudioBlocked = () => false;
  this.appendTo = (newParentDomElement) => {
    parentDomElement = newParentDomElement;
    return this;
  };

  // Bind a stream to the video element.
  this.bindToStream = function bindToStream(webRtcStream, completion) {
    if (typeof completion === 'function') {
      console.warn('bindToStream callback argument is deprecated, use the returned promise');
    }
    const promise = new Promise((resolve, reject) => {
      if (!parentDomElement || !document.body.contains(parentDomElement)) {
        logging.error('The VideoElement must be attached to a DOM node before a stream can be bound');
      }
      pluginVideoContainer = webRtcStream._.render();
      this.trigger('videoElementCreated', this.domElement());
      pluginVideoContainer.setFitMode(fitMode);
      pluginVideoContainer.appendTo(parentDomElement);
      pluginVideoContainer.show((error) => {
        if (error) {
          reject(error);
          return;
        }

        if (webRtcStream.hasVideo() && !this.videoWidth()) {
          // Wait for video dimensions but wait a maximum of TIMEOUT seconds
          let dimensionTimeout;
          const callCompletion = () => {
            this.off('videoDimensionsChanged', callCompletion);
            clearTimeout(dimensionTimeout);
            dimensionTimeout = null;
            resolve();
          };
          this.on('videoDimensionsChanged', callCompletion);
          dimensionTimeout = setTimeout(callCompletion, TIMEOUT);
        } else {
          resolve();
        }
      });

      let listener;
      const onAllEnded = () => {
        listener.stop();
        this.trigger('mediaStopped');
      };

      const onSingleEnded = (track) => {
        this.trigger('mediaStopped', track);
      };

      // Note: This can only recognise the *current* tracks of the stream have ended. If the stream
      // tracks change, this will need to be updated.
      listener = listenForTracksEnded(webRtcStream, onAllEnded, onSingleEnded);
      this.trigger('_bind');
    });

    promise
      .catch(err => completion(err))
      .then(() => completion(null));

    return promise;
  };

  // Unbind the currently bound stream from the video element.
  this.unbindStream = () => {
    // TODO: some way to tell OTPlugin to release that stream and controller

    this.trigger('_unbind');
    if (pluginVideoContainer) {
      pluginVideoContainer.destroy();
      if (this.domElement() && this.domElement().parentNode) {
        this.domElement().parentNode.removeChild(this.domElement());
      }
      parentDomElement = null;
      pluginVideoContainer = null;
    }

    return this;
  };

  this.destroy = () => {
    this.unbindStream();
    this.trigger('destroyed');
    return undefined;
  };

  this.setAudioVolume = (value) => {
    pluginVideoContainer.setAudioVolume(parseFloat(value));
  };

  this.getAudioVolume = () => pluginVideoContainer.getAudioVolume();

  // see https://wiki.mozilla.org/WebAPI/AudioChannels
  // The audioChannelType is not currently supported in the plugin.
  this.audioChannelType = () => 'unknown';

  this.getAudioInputLevel = () => Promise.resolve(pluginVideoContainer.getAudioInputLevel());

  this.whenTimeIncrements = (callback, context) => {
    // exists for compatibility with NativeVideoElement
    setTimeout(callback.bind(context));
  };

  eventing(this);
  canBeOrientatedMixin(this, () => pluginVideoContainer.domElement, orientationChangedHandler);

  const watchForResize = new Watcher(
    () => ({ width: this.videoWidth(), height: this.videoHeight() }),
    (res, oldRes) => {
      this.trigger('videoDimensionsChanged', oldRes, res);
    }
  );

  this.on('_bind', () => watchForResize.start());
  this.on('_unbind', () => watchForResize.stop());
}

module.exports = PluginVideoElementWrapper;



// WEBPACK FOOTER //
// ./src/js/helpers/video_element/plugin_video_element_wrapper.js